import React, { useEffect, useState } from 'react'
import { IEpiProps, IEventRegistrationRefreshModel, IRenewalEligibilityResponse, StepConstants } from '../models/event-reg-refresh-models'
import { useEventRegRefreshContext } from '../reducers/event-reg-refresh-reducers'
import { Alert } from './alert'
import { LoadingSpinner } from './loading-spinner'

interface IContextProps {
  epiProps: IEpiProps
  currentStep: string
  eventRegistrationRefreshModel: IEventRegistrationRefreshModel
}

interface IProps {
  specialtyCode: string
}

export const RenewalEligibility = ({ specialtyCode }: IProps) => {
  const { epiProps, currentStep, eventRegistrationRefreshModel }: IContextProps = useEventRegRefreshContext()!
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [result, setResult] = useState<IRenewalEligibilityResponse | null>(null)
  const [showCheckAdditionalAttendeeCtaText, setShowCheckAdditionalAttendeeCtaText] = useState(false);
  const [errorMessage, setErrorMessage] = useState('')

  // Reset form state when specialtyCode changes
  useEffect(() => {
    setEmail('')
    setResult(null)
    setErrorMessage('')
    setShowCheckAdditionalAttendeeCtaText(false)
  }, [specialtyCode])

  // Get the specialty program name from eventRegistrationRefreshModel
  const getSpecialtyProgramName = (): string => {
    if (!eventRegistrationRefreshModel || !eventRegistrationRefreshModel.specialtyPrograms) {
      return specialtyCode;
    }

    // Find the specialty program that matches the specialtyCode
    const matchingProgram = eventRegistrationRefreshModel.specialtyPrograms.find(
      program => program.programCode === specialtyCode && program.isRenewal == true
    );

    return matchingProgram ? matchingProgram.programName : specialtyCode;
  };

  // Get the specialty program name
  const specialtyProgramName = getSpecialtyProgramName();

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    // Clear previous results when input changes
    setResult(null)
    setErrorMessage('')
  }

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    return emailRegex.test(email)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Basic validation
    if (!email || !validateEmail(email)) {
      setErrorMessage('Please enter a valid email address')
      return
    }

    try {
      setIsSubmitting(true)
      setErrorMessage('')
      setResult(null)

      const response = await fetch('/internalapi/EventRegistrationRefreshApi/CheckSingleEmailRenewalEligibility', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          specialtyCode: specialtyCode
        })
      })

      if (!response.ok) {
        throw new Error(`API call failed with status: ${response.status}`)
      }

      const data = await response.json()
      setResult(data)
    } catch (err) {
      console.error('Error checking renewal eligibility:', err)
      setErrorMessage('An error occurred while checking eligibility. Please try again.')
    } finally {
      setIsSubmitting(false)
      setShowCheckAdditionalAttendeeCtaText(true)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-item">
        {result && (
          <>
            {
              result.isError &&
              (
                <Alert>
                  <div dangerouslySetInnerHTML={{
                    __html: epiProps.eligibilityCheckErrorMessage
                  }} />
                </Alert>
              )
            }
            {result.isEligible && (
              <Alert variant="success" icon="success">
                <div dangerouslySetInnerHTML={{
                  __html: epiProps.eligibleMessage.replace(/EMAIL/g, result.email)
                }} />
              </Alert>
            )}
            {
              !result.isEligible && !result.isError &&
              (
                <Alert icon="error">
                  <div dangerouslySetInnerHTML={{
                    __html: epiProps.notEligibleMessage.replace(/EMAIL/g, result.email)
                  }} />
                </Alert>
              )
            }
          </>
        )}

        {errorMessage && (
          <Alert>
            {errorMessage}
          </Alert>
        )}

        <label htmlFor="email">Email Address:</label>
        <input
          className="fullwidth-input"
          type="email"
          id="email"
          value={email}
          onChange={handleEmailChange}
          placeholder="Enter email to check"
          disabled={isSubmitting}
        />
      </div>

      <button
        className="btn-primary form-item"
        type="submit"
        disabled={isSubmitting}
      >
        {isSubmitting
          ? epiProps.checkingEligibilityCtaText
          : showCheckAdditionalAttendeeCtaText
            ? epiProps.checkEligibilityCtaTextAdditionalAttendee
            : epiProps.checkEligibilityCtaText
        } {isSubmitting && <LoadingSpinner />}
      </button>
    </form>
  )
}
